// pages/Settings.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import api from '../services/api';
import { getIcon } from '../utils/icons';

/**
 * Страница настроек системы
 * @returns {JSX.Element} - Компонент для рендеринга
 */
const SettingsPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [aiEnabled, setAiEnabled] = useState(true);
  const [licenseKey, setLicenseKey] = useState('');
  const [aiUsageStats, setAiUsageStats] = useState({
    totalRecognitions: 0,
    successfulRecognitions: 0,
    failedRecognitions: 0,
    averageConfidence: 0,
    last7Days: []
  });

  useEffect(() => {
    fetchSettings();
    fetchAiUsageStats();
  }, []);

  /**
   * Загрузка настроек системы
   */
  const fetchSettings = async () => {
    try {
      setLoading(true);

      // Получаем статус AI
      const aiStatusResponse = await api.get('/api/v1/system-settings/ai-enabled');
      setAiEnabled(aiStatusResponse.data.value === 1);

      // Получаем лицензионный ключ
      try {
        const licenseResponse = await api.get('/api/v1/system-settings/license-key');
        setLicenseKey(licenseResponse.data.value || '');
      } catch (licenseErr) {
        console.log('Лицензионный ключ не найден');
        setLicenseKey('');
      }
    } catch (error) {
      console.error('Ошибка при загрузке настроек:', error);
      toast.error('Не удалось загрузить настройки системы');
    } finally {
      setLoading(false);
    }
  };

  /**
   * Загрузка статистики использования AI за 7 дней
   */
  const fetchAiUsageStats = async () => {
    try {
      const response = await api.get('/api/v1/ai/usage-stats?days=7');
      setAiUsageStats(response.data);
    } catch (error) {
      console.error('Ошибка при загрузке статистики AI:', error);
      // Не показываем ошибку пользователю, так как это дополнительная информация
      setAiUsageStats({
        totalRecognitions: 0,
        successfulRecognitions: 0,
        failedRecognitions: 0,
        averageConfidence: 0,
        last7Days: []
      });
    }
  };

  /**
   * Переключение статуса AI
   */
  const handleToggleAi = async () => {
    try {
      setSaving(true);
      const newValue = !aiEnabled;

      await api.post('/api/v1/system-settings/ai-enabled', {
        value: newValue ? 1 : 0
      });

      setAiEnabled(newValue);
      toast.success(`Система AI ${newValue ? 'включена' : 'отключена'}`);
    } catch (error) {
      console.error('Ошибка при изменении статуса AI:', error);
      toast.error('Не удалось изменить статус AI');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Сохранение лицензионного ключа
   */
  const handleSaveLicenseKey = async () => {
    // Валидация UUID формата
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

    if (licenseKey && !uuidRegex.test(licenseKey)) {
      toast.error('Неверный формат лицензионного ключа. Ожидается формат UUID');
      return;
    }

    try {
      setSaving(true);

      await api.post('/api/v1/system-settings/license-key', {
        value: licenseKey
      });

      toast.success('Лицензионный ключ сохранен');
    } catch (error) {
      console.error('Ошибка при сохранении лицензионного ключа:', error);
      toast.error('Не удалось сохранить лицензионный ключ');
    } finally {
      setSaving(false);
    }
  };

  /**
   * Форматирование процента успешности
   */
  const getSuccessRate = () => {
    if (aiUsageStats.totalRecognitions === 0) return 0;
    return ((aiUsageStats.successfulRecognitions / aiUsageStats.totalRecognitions) * 100).toFixed(1);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div>
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-gray-800">Настройки системы</h1>
        <p className="text-gray-600">Управление настройками и конфигурацией системы</p>
      </header>

      <div className="space-y-6">
        {/* Блок управления AI */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-start justify-between mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800 mb-2">
                Система AI распознавания
              </h2>
              <p className="text-sm text-gray-600">
                Включение и отключение автоматического распознавания текста на изображениях
              </p>
            </div>

            {/* Toggle Switch */}
            <button
              onClick={handleToggleAi}
              disabled={true}
              className={`relative inline-flex h-8 w-14 items-center rounded-full transition-colors opacity-50 cursor-not-allowed ${
                aiEnabled ? 'bg-green-500' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-6 w-6 transform rounded-full bg-white transition-transform ${
                  aiEnabled ? 'translate-x-7' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          <div className="flex items-center mb-4">
            <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
              aiEnabled ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
            }`}>
              <span className={`h-2 w-2 rounded-full mr-2 ${aiEnabled ? 'bg-green-500' : 'bg-gray-500'}`}></span>
              {aiEnabled ? 'Активна' : 'Неактивна'}
            </div>
          </div>

          {/* Статистика AI за 7 дней */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <h3 className="text-md font-semibold text-gray-800 mb-4">
              Использование системы распознавания за 7 дней
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="bg-blue-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Всего распознаваний</p>
                    <p className="text-2xl font-bold text-gray-900">{aiUsageStats.totalRecognitions}</p>
                  </div>
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
              </div>

              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Успешных</p>
                    <p className="text-2xl font-bold text-gray-900">{aiUsageStats.successfulRecognitions}</p>
                  </div>
                  <svg className="w-8 h-8 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
              </div>

              <div className="bg-red-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Неудачных</p>
                    <p className="text-2xl font-bold text-gray-900">{aiUsageStats.failedRecognitions}</p>
                  </div>
                  <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
              </div>

              <div className="bg-purple-50 rounded-lg p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">Успешность</p>
                    <p className="text-2xl font-bold text-gray-900">{getSuccessRate()}%</p>
                  </div>
                  <svg className="w-8 h-8 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                </div>
              </div>
            </div>

            {/* График использования по дням */}
            {aiUsageStats.last7Days && aiUsageStats.last7Days.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Динамика использования</h4>
                <div className="flex items-end justify-between h-32 gap-2">
                  {aiUsageStats.last7Days.map((day, index) => {
                    const maxValue = Math.max(...aiUsageStats.last7Days.map(d => d.count), 1);
                    const height = (day.count / maxValue) * 100;

                    return (
                      <div key={index} className="flex flex-col items-center flex-1">
                        <div className="w-full bg-blue-500 rounded-t hover:bg-blue-600 transition-colors relative group" style={{ height: `${height}%`, minHeight: '4px' }}>
                          <div className="absolute bottom-full mb-2 hidden group-hover:block bg-gray-800 text-white text-xs rounded py-1 px-2 whitespace-nowrap">
                            {day.count} распознаваний
                          </div>
                        </div>
                        <span className="text-xs text-gray-500 mt-2">
                          {new Date(day.date).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Блок лицензионного ключа */}
        <div className="bg-white rounded-lg shadow-sm p-6 opacity-75">
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-800 mb-2">
              Лицензионный ключ
            </h2>
            <p className="text-sm text-gray-600">
              Специальная версия для Администрации Тюмени
            </p>
          </div>

          <div className="space-y-4">
            <div>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={licenseKey}
                  onChange={(e) => setLicenseKey(e.target.value)}
                  placeholder="00000000-0000-0000-0000-000000000000"
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 font-mono text-sm bg-gray-100 cursor-not-allowed"
                  disabled={true}
                />
                <button
                  onClick={handleSaveLicenseKey}
                  disabled={true}
                  className="bg-gray-400 text-white px-6 py-2 rounded-md font-medium transition-colors opacity-50 cursor-not-allowed"
                >
                  Сохранить
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Данная версия системы специально разработана для Администрации города Тюмени
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <h4 className="text-sm font-medium text-blue-900">Специальная версия</h4>
                  <p className="text-xs text-blue-700 mt-1">
                    Система работает в специальном режиме для Администрации города Тюмени. Лицензионный ключ не требуется.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
